import type { CSSProperties } from "react";
import TaskStatusControls, { type PersistedTaskStatus } from "@/components/tasks/status/TaskStatusControls/TaskStatusControls";
import TaskTimingActions from "@/components/tasks/timing/TaskTimingActions/TaskTimingActions";
import TaskTimingFields, { type TaskTimingBinding, type TaskTimingColors } from "@/components/tasks/timing/TaskTimingFields/TaskTimingFields";
import TaskTimingHeader from "@/components/tasks/timing/TaskTimingHeader/TaskTimingHeader";
import type { TaskLifecycle } from "@shared/taskLifecycle";

export type TaskTimingSectionProps = {
  kind: "schedule" | "status";
  className?: string;
  colors: TaskTimingColors;
  open: boolean;
  expandable: boolean;
  onOpenChange: (open: boolean) => void;
  timing: TaskTimingBinding;
  headerTiming?: TaskTimingBinding["value"];
  label?: string;
  lifecycle?: TaskLifecycle;
  emptyLabel?: string;
  status?: PersistedTaskStatus;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  onStatusChange?: (status: PersistedTaskStatus) => void;
  alertEnabled?: boolean;
  onAlertEnabledChange?: (value: boolean) => void;
  onToday: () => void;
  onNow: () => void;
  onClear: () => void;
};

export default function TaskTimingSection({
  kind,
  className = "",
  colors,
  open,
  expandable,
  onOpenChange,
  timing,
  headerTiming,
  label,
  lifecycle,
  emptyLabel,
  status,
  startedAt,
  completedAt,
  onStatusChange,
  alertEnabled,
  onAlertEnabledChange,
  onToday,
  onNow,
  onClear,
}: TaskTimingSectionProps) {
  const style = {
    "--section-bar": colors.bar,
    "--section-soft": colors.soft,
    "--section-ink": colors.ink,
  } as CSSProperties;

  return (
    <section className={`task-timing-section ${className}`.trim()} style={style}>
      <TaskTimingHeader
        kind={kind}
        timing={headerTiming ?? timing.value}
        label={label}
        lifecycle={lifecycle}
        emptyLabel={emptyLabel}
        alertEnabled={alertEnabled}
        open={open}
        expandable={expandable}
        onClick={() => onOpenChange(!open)}
        color={colors.ink}
      />
      {kind === "status" && status && onStatusChange && (
        <TaskStatusControls
          status={status}
          startedAt={startedAt}
          completedAt={completedAt}
          onStatusChange={onStatusChange}
          colors={colors}
        />
      )}
      <div className={`timing-body ${open ? "is-open" : "is-closed"}`}>
        <TaskTimingFields
          {...timing}
          colors={colors}
          alertEnabled={alertEnabled}
          onAlertEnabledChange={onAlertEnabledChange}
        />
      </div>
      <TaskTimingActions onToday={onToday} onNow={onNow} onClear={onClear} color={colors.ink} />
    </section>
  );
}
